import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";

type Props = {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "section" | "article";
  initial?: boolean;
  style?: CSSProperties;
};

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
  initial = false,
  style,
}: Props) {
  const reduce = prefersReducedMotion();
  const [visible, setVisible] = useState(initial || reduce);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reduce) {
      setVisible(true);
      return;
    }
    if (initial) {
      const id = window.setTimeout(() => setVisible(true), delay);
      return () => window.clearTimeout(id);
    }
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            window.setTimeout(() => setVisible(true), delay);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "0px 0px -80px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [delay, initial, reduce]);

  return (
    <Tag
      ref={ref as any}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible || reduce ? "none" : "translateY(12px)",
        transition: reduce
          ? "none"
          : "opacity 500ms ease-out, transform 500ms ease-out",
        willChange: visible || reduce ? "auto" : "opacity, transform",
      }}
      className={className}
    >
      {children}
    </Tag>
  );
}
